import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DefaultConfiguration } from '@models/document/default-configuration.const';
import { DelegationConfiguration } from '@models/document/delegation-configuration.const';
import { InOutType } from '@models/generic/in-out-type.enum';
import { DocumentDto } from '@models/protocols/document.dto';
import { DelegateSignerDto, DelegationDto } from '@models/users/delegation.dto';
import { Sectors, TypeContracts } from '@models/users/role.const';
import { AccountService } from '@services/account.service';
import { DocumentService } from '@services/document.service';
import { LoadingService } from '@services/loading.service';
import { MessageService } from '@services/message.service';
import { ProtocolService } from '@services/protocol.service';
import { SubscriberService } from '@services/subscriber.service';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { catchError } from 'rxjs';
import { DelegationConfirmComponent } from '../delegation-confirm/delegation-confirm.component';

@Component({
  selector: 'ebs-delegation-dialog',
  templateUrl: './delegation-dialog.component.html',
  styleUrls: ['./delegation-dialog.component.scss'],
})
export class DelegationDialogComponent implements OnInit {
  formGroup!: FormGroup;

  readonly sectorRoleString = {
    'A. D. E.': 'ADDETTO AL SERVIZIO',
    'O. D. E.': 'OPERATORE DI ESERCIZIO',
    Impiegato: 'IMPIEGATO',
    'O. O.': 'OPERATORE OFFICINA',
    Nessuno: 'NESSUNO',
  };

  availableFonts = [
    'bastliga',
    'broetown',
    'calvin-fallen',
    'corinthiago',
    'lihataja',
    'qwigley',
    'starlights',
    'washington',
  ];
  selectedFont: string = 'bastliga';

  /* @ViewChild('whiteboard')
  whiteboard: NgWhiteboardComponent | undefined; */

  public signer: DelegateSignerDto = {
    delegateName: '',
    delegateRole: '',
    fontFamily: '',
    showSign: true,
    subscriptionDate: new Date(),
    subscriptionPlace: '',
  };

  public newSubscriber: DelegationDto = {
    email: '',
    headquarter: '',
    lastName: '',
    name: '',
    phone: '',
    registerNumber: '',
    role: 'Iscritto',
    sector: '',
    contract: 'AUTOLINEE TOSCANE IN PISTOIA \"EX - COPIT S.P.A.\"',
    externalCode: this.randomString(
      6,
      '01234567890123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    ),
    typeContract: '',
    birthCity: '',
    birthDate: new Date(),
    birthPlace: '',
    fiscalCode: '',
    residentAddress: '',
    isExternal: false,
    residentCity: '',
    residentPlace: '',
  };

  randomString(length, chars) {
    let result = '';
    for (let i = length; i > 0; --i) {
      let variable = Math.floor(Math.random() * chars.length);
      result += chars[variable];
    }
    return result;
  }

  filteredSectors: string[] = [];
  filteredTypeContracts: string[] = [];

  public get sectors(): string[] {
    return Sectors;
  }

  public get typeContracts(): string[] {
    return TypeContracts;
  }

  constructor(
    private subscriberService: SubscriberService,
    public ref: DynamicDialogRef,
    public dialogService: DialogService,
    private messageService: MessageService,
    private protocolService: ProtocolService,
    private documentService: DocumentService,
    private accountService: AccountService,
    private loader: LoadingService,
  ) {
    this.formGroup = new FormGroup({
      email: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      headquarter: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      lastName: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      name: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      phone: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      registerNumber: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      sector: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      contract: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      birthDate: new FormControl<Date>(
        new Date(),
        Validators.compose([Validators.required]),
      ),
      birthPlace: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      birthCity: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      residentPlace: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      residentCity: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      residentAddress: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      fiscalCode: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      typeContract: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
    });
  }

  ngOnInit(): void {}

  showConfirm() {
    let dcref = this.dialogService.open(DelegationConfirmComponent, {
      closable: false,
    });

    dcref.onClose.subscribe((result) => {
      if (!result) {
        return;
      }
      this.signer = result.signer;
      this.onConfirmationDone();
    });
  }

  onConfirmationDone() {
    if (this.newSubscriber !== null) {
      this.loader.show();
      this.subscriberService
        .createSubscriber([this.newSubscriber])
        .pipe(
          catchError((err) => {
            this.messageService.error(
              'Nuovo iscritto',
              "Errore durante l'inserimento, riprovare",
            );
            this.ref.close(false);
            throw err;
          }),
        )
        .subscribe(async (result) => {
          this.messageService.success(
            'Nuovo iscritto',
            'Inserimento avvenuto con successo',
          );

          let values = {
            email: this.newSubscriber.email,
            headquarter: this.newSubscriber.headquarter,
            name: `${this.newSubscriber.name} ${this.newSubscriber.lastName}`,
            phone: this.newSubscriber.phone,
            registerNumber: this.newSubscriber.registerNumber,
            sector: `${this.sectorRoleString[this.newSubscriber.sector]} ${this.newSubscriber.contract}`,
            birthDate: this.newSubscriber.birthDate.toLocaleDateString(
              'it-IT',
              {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              },
            ),
            birthPlace: this.newSubscriber.birthPlace,
            birthCity: this.newSubscriber.birthCity,
            residentPlace: this.newSubscriber.residentPlace,
            residentCity: this.newSubscriber.residentCity,
            residentAddress: this.newSubscriber.residentAddress,
            fiscalCode: this.newSubscriber.fiscalCode.toUpperCase(),
            subscriberSign: `${this.newSubscriber.name} ${this.newSubscriber.lastName}`,
            nameSign:
              `${this.newSubscriber.name} ${this.newSubscriber.lastName}`.toUpperCase(),
            delegateSign: this.signer.showSign ? this.signer.delegateName : '',
            delegateName: `${this.signer.delegateRole} ${this.signer.delegateName}`,
            subscriptionDate: this.signer.subscriptionDate.toLocaleDateString(
              'it-IT',
              {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              },
            ),
            subscriptionPlace: this.signer.subscriptionPlace,
          };

          let configuration = DelegationConfiguration;
          configuration['subscriberSign'] = configuration['subscriberSign'];
          configuration['subscriberSign'].family = this.selectedFont;
          configuration['delegateSign'] = configuration['delegateSign'];
          configuration['delegateSign'].family = this.signer.fontFamily;

          let delegationBlob = await this.documentService.writePdfFromUrl(
            'assets/documents/delegation.pdf',
            configuration,
            values,
          );
          let firstUpload = {
            name: `Delega Sindacale ${this.newSubscriber.name} ${this.newSubscriber.lastName}.pdf`,
            hasProtocol: false,
            isSigned: false,
            mimeType: 'application/pdf',
          };
          this.documentService
            .uploadFile(firstUpload, delegationBlob)
            .subscribe((firstReturn: DocumentDto) => {
              this.protocolService
                .createProtocol([
                  {
                    document: firstReturn.docId!,
                    type: InOutType.OUT,
                  },
                ])
                .subscribe(async (result) => {
                  let blob = await this.documentService.writePdfFromBuffer(
                    delegationBlob,
                    DefaultConfiguration,
                    {
                      protocol: `Protocollo N. ${result!.protocolId} del ${new Date().toLocaleDateString(
                        'it-IT',
                        {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        },
                      )}`,
                      orgName: 'Segreteria Provinciale FAISA-CISAL di Pistoia',
                      type: `${result!.type == InOutType.IN ? 'E' : 'U'}`,
                    },
                  );
                  this.documentService
                    .uploadFile(result.document, blob)
                    .subscribe((res) => {
                      this.loader.hide();
                      this.documentService
                        .downloadFile(res)
                        .subscribe(async (blobResponse: any) => {
                          let downloadLink = document.createElement('a');
                          downloadLink.href =
                            window.URL.createObjectURL(blobResponse);
                          if (firstReturn.name)
                            downloadLink.setAttribute(
                              'download',
                              firstReturn.name,
                            );
                          document.body.appendChild(downloadLink);
                          downloadLink.click();
                          document.body.removeChild(downloadLink);
                          this.ref.close(true);
                        });
                    });
                });
            });
        });
    }
  }

  cancel() {
    this.ref.close(false);
  }

  //filterSubscriber(event: AutoCompleteCompleteEvent) { //vecchio nome dato da Francesco Mati
  filterSector(event: AutoCompleteCompleteEvent) {
    let filtered: any[] = [];
    let query = event.query;

    for (let i = 0; i < this.sectors.length; i++) {
      let subscriber = this.sectors[i];
      if (subscriber.toLowerCase().indexOf(query.toLowerCase()) > -1) {
        filtered.push(subscriber);
      }
    }

    this.filteredSectors = filtered;
  }

  filterTypeContract(event: AutoCompleteCompleteEvent) {
    let filtered: any[] = [];
    let query = event.query;

    for (let i = 0; i < this.typeContracts.length; i++) {
      let subscriber = this.typeContracts[i];
      if (subscriber.toLowerCase().indexOf(query.toLowerCase()) > -1) {
        filtered.push(subscriber);
      }
    }

    this.filteredTypeContracts = filtered;
  }
}
